/* Our Story (discover) page: motion for the headings and illustrations.

   Updated 24/09/2026 so the illustrations move more, and more smoothly:
   - Scroll-linked motion glides after the scroll (scrub: SMOOTH) instead of snapping to it.
   - Offsets are in percent of each illustration's own size, so the movement reads the same on
     phones, laptops and large screens.
   - New choreography for the bottle and glass (they swing together for a cheers, then part),
     the mango (tossed up in an arc) and the brass lota (tips forward and pours as it comes into
     view).
   - Illustrations float gently while on screen; the loops pause when off screen.
   - Scroll positions are re-measured as the lazy images load. Before, the page grew by about
     500px after the first measurement, so the lower animations had mostly played before they
     came into view.
   - Visitors whose device asks for reduced motion get the illustrations at rest. */
document.addEventListener("DOMContentLoaded", () => {
  if (typeof gsap === "undefined" || typeof ScrollTrigger === "undefined") return;
  gsap.registerPlugin(ScrollTrigger);

  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const isMobile = window.matchMedia("(max-width: 767px)").matches;
  const SMOOTH = 1; // seconds for the scroll-linked motion to catch up with the scroll

  /* ---------- helpers ---------- */

  // Re-measure the scroll positions whenever the page changes height (lazy images arriving).
  let refreshTimer;
  const queueRefresh = () => {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(() => ScrollTrigger.refresh(), 200);
  };
  document.addEventListener("load", e => { if (e.target.tagName === "IMG") queueRefresh(); }, true);
  if ("ResizeObserver" in window) new ResizeObserver(queueRefresh).observe(document.body);

  // 30/09/2026: on large screens the story cards and quotes are drawn a little larger with CSS zoom
  // (the wide page layout in assets/animations-gelato.css). Inside a zoomed box, offsetTop and
  // offsetHeight come back in the box's own, smaller units, so the scroll ranges came out too short
  // and too early. zoomOf() is the total zoom on an element (1 where nothing is zoomed, so smaller
  // screens are unchanged). It is worked out once per re-measure.
  let zoomCache = new WeakMap();
  ScrollTrigger.addEventListener("refreshInit", () => { zoomCache = new WeakMap(); });
  const zoomOf = el => {
    if (!el || el.nodeType !== 1) return 1;
    let z = zoomCache.get(el);
    if (z === undefined) {
      const own = parseFloat(getComputedStyle(el).zoom);
      z = (own > 0 ? own : 1) * zoomOf(el.parentElement);
      zoomCache.set(el, z);
    }
    return z;
  };
  // Top of an element's layout box on the page. Transforms are ignored, so an illustration's
  // own movement never shifts the scroll range that drives it.
  const pageTop = el => {
    let y = 0;
    for (let node = el; node; node = node.offsetParent) y += node.offsetTop * zoomOf(node);
    return y;
  };
  // Scroll range from when the element's top is `enter` of the way down the screen until its
  // bottom is `leave` of the way down (1 = bottom edge of the screen, 0 = top edge).
  const range = (el, enter = 1, leave = 0) => ({
    start: () => pageTop(el) - window.innerHeight * enter,
    end: () => pageTop(el) + el.offsetHeight * zoomOf(el) - window.innerHeight * leave
  });
  const scrubbed = (el, enter, leave) => Object.assign({ scrub: SMOOTH }, range(el, enter, leave));

  // Run a looping tween only while `el` is on screen.
  const whileVisible = (el, tween) => {
    ScrollTrigger.create(Object.assign(range(el), {
      onToggle: self => (self.isActive ? tween.play() : tween.pause())
    }));
    return tween;
  };
  // A gentle float. It moves `y`, while the scroll-linked motion moves xPercent and yPercent,
  // so the two add up instead of fighting.
  const float = (el, vars, host) => {
    if (reduceMotion || !el) return;
    whileVisible(host || el, gsap.to(el, Object.assign({
      y: -10, duration: 2.8, ease: "sine.inOut", yoyo: true, repeat: -1, paused: true
    }, vars)));
  };

  /* ---------- headings ---------- */

  if (typeof SplitType !== "undefined") {
    // Hero heading, letter by letter
    const hero = document.querySelector("h2.heading-style-display.text-color-secondary:not(.is-discover)");
    if (hero) {
      const split = new SplitType(hero, { types: "chars" });
      gsap.from(split.chars, { opacity: 0, y: 30, stagger: 0.03, duration: 0.7, ease: "power2.out" });
    }

    // Quotes, line by line
    document.querySelectorAll(".layout_large-quote .heading-style-display.text-color-secondary.is-discover")
      .forEach(quote => {
        const split = new SplitType(quote, { types: "lines" });
        gsap.from(split.lines, {
          scrollTrigger: { trigger: quote, start: "top 80%" },
          yPercent: 100,
          opacity: 0,
          stagger: 0.1,
          ease: "sine.out",
          duration: 1
        });
      });
  }

  /* ---------- illustrations ---------- */

  // Sun character: fades up once, then a slow bob while on screen
  const tiger = document.querySelector(".illo_tiger");
  if (tiger) {
    gsap.fromTo(tiger, { opacity: 0, y: reduceMotion ? 0 : 70 }, {
      opacity: 1,
      y: 0,
      delay: 0.8,
      duration: 1.2,
      ease: "sine.out",
      onComplete: () => {
        if (reduceMotion) return;
        whileVisible(tiger, gsap.to(tiger, { y: 38, duration: 2.6, ease: "sine.inOut", yoyo: true, repeat: -1, paused: true }));
      }
    });
  }

  if (!reduceMotion) {
    // Founders' photo and names
    document.querySelectorAll(".mohit-image").forEach(el => {
      gsap.from(el, { rotation: -20.5, transformOrigin: "bottom left", ease: "sine.out", scrollTrigger: scrubbed(el, 0.8, 0) });
    });
    document.querySelectorAll(".mohit-name").forEach(el => {
      gsap.fromTo(el, { y: "-3rem" }, { y: "2rem", ease: "sine.out", scrollTrigger: scrubbed(el, 0.9, 0) });
    });
    document.querySelectorAll(".mohit-aneet").forEach(el => {
      gsap.fromTo(el, { y: "3rem" }, { y: "-1rem", ease: "sine.out", scrollTrigger: scrubbed(el, 0.9, 0) });
    });

    // Diamond and oval badges
    document.querySelectorAll(".is-diamon").forEach(el => {
      gsap.from(el, { x: isMobile ? -60 : 0, y: isMobile ? 0 : 50, ease: "sine.out", scrollTrigger: scrubbed(el, 0.9, 0) });
    });
    document.querySelectorAll(".is-oval").forEach(el => {
      gsap.from(el, { x: isMobile ? 60 : 0, y: isMobile ? 0 : -50, ease: "sine.out", scrollTrigger: scrubbed(el, 0.9, 0) });
    });

    // Lotus: drifts across and turns a little
    document.querySelectorAll(".illo_lotus").forEach(el => {
      gsap.fromTo(el, { x: -50, rotation: -8 }, { x: 30, rotation: 6, ease: "sine.inOut", scrollTrigger: scrubbed(el, 0.8, 0) });
      float(el, { y: -10, duration: 3 });
    });

    // Mango: tossed up in an arc. It rises and turns into place as it reaches the middle of the
    // screen, then carries on up and away. The top left mango on phones and tablets mirrors it.
    document.querySelectorAll(".illo_mango").forEach(el => {
      const dir = el.classList.contains("mobile-only") ? -1 : 1;
      gsap.timeline({ scrollTrigger: scrubbed(el) })
        .fromTo(el, { xPercent: 45 * dir, yPercent: 65, rotation: 55 * dir, scale: 0.8 },
          { xPercent: 0, yPercent: 0, rotation: 0, scale: 1, ease: "power2.out", duration: 1 })
        .to(el, { xPercent: -28 * dir, yPercent: -45, rotation: -22 * dir, ease: "sine.in", duration: 1 });
      float(el, { y: -9, duration: 2.4 });
    });
  }

  // Bottle and glass: they rise in leaning apart, swing together for a cheers as the card reaches
  // the middle of the screen (the bottle tips on its base, the glass on the wrist), then part.
  document.querySelectorAll(".about_image-wrapper").forEach(wrapper => {
    const bottle = wrapper.querySelector(".is-bottle");
    const hand = wrapper.querySelector(".is-hand");
    if (!bottle || !hand) return;
    gsap.set(bottle, { zIndex: 2, transformOrigin: "50% 88%" });
    gsap.set(hand, { zIndex: 1, transformOrigin: "78% 100%" });
    if (reduceMotion) return;
    gsap.timeline({ defaults: { ease: "sine.inOut", duration: 1 }, scrollTrigger: scrubbed(wrapper, 0.95, 0.05) })
      .fromTo(bottle, { xPercent: -6, yPercent: 16, rotation: -14, scale: 0.92 },
        { xPercent: 2, yPercent: -2, rotation: 15, scale: 1 }, 0)
      .fromTo(hand, { xPercent: 12, yPercent: 18, rotation: 16, scale: 0.92 },
        { xPercent: 1, yPercent: -3, rotation: -4, scale: 1 }, 0)
      .to(bottle, { xPercent: -2, yPercent: -8, rotation: -5 }, 1)
      .to(hand, { xPercent: 5, yPercent: -7, rotation: 7 }, 1);
    float(bottle, { y: -9, duration: 3.2 }, wrapper);
    float(hand, { y: -12, duration: 2.6, delay: 0.6 }, wrapper);
  });

  // Any glass without a bottle beside it
  if (!reduceMotion) {
    document.querySelectorAll(".is-hand").forEach(el => {
      if (el.parentElement && el.parentElement.querySelector(".is-bottle")) return;
      gsap.from(el, { x: isMobile ? (Math.random() > 0.5 ? -60 : 60) : 0, y: isMobile ? 0 : 50, ease: "sine.out", scrollTrigger: scrubbed(el, 0.9, 0) });
    });
  }

  // Brass lota: it glides in upright, tips forward as it rises up the screen, and the stream of
  // Himalayan water runs down from the lip, so the pour is complete by the middle of the screen.
  // The sparkles twinkle while it is on screen.
  document.querySelectorAll(".illo_lota").forEach(lota => {
    if (reduceMotion) return;
    const pot = lota.querySelector(".illo_lota__pot");
    const water = lota.querySelector(".illo_lota__water");
    if (!pot || !water) {
      // single image version
      gsap.fromTo(lota, { rotation: 10, yPercent: 10 }, { rotation: -6, yPercent: -10, ease: "sine.inOut", scrollTrigger: scrubbed(lota) });
      float(lota, { y: -8, duration: 2.6 });
      return;
    }
    gsap.timeline({ scrollTrigger: scrubbed(lota) })
      .fromTo(lota, { xPercent: 16, yPercent: 24 }, { xPercent: 0, yPercent: 0, ease: "power2.out", duration: 0.3 }, 0)
      .fromTo(pot, { rotation: 32 }, { rotation: 0, ease: "sine.inOut", duration: 0.27 }, 0.05)
      .fromTo(water, { clipPath: "inset(0% 0% 78% 0%)" }, { clipPath: "inset(0% 0% 0% 0%)", ease: "power1.in", duration: 0.22 }, 0.24)
      .to(pot, { rotation: -5, ease: "sine.inOut", duration: 0.54 }, 0.46)
      .to(lota, { yPercent: -16, ease: "sine.in", duration: 0.54 }, 0.46);
    float(lota, { y: -8, duration: 2.6 });
    const stars = lota.querySelectorAll(".illo_lota__sparkles path");
    if (stars.length) {
      whileVisible(lota, gsap.fromTo(stars, { scale: 1, rotation: 0 }, {
        scale: 0.45,
        rotation: 45,
        transformOrigin: "50% 50%",
        duration: 0.9,
        ease: "sine.inOut",
        stagger: { each: 0.45, repeat: -1, yoyo: true },
        paused: true
      }));
    }
  });

  // Column still: revealed from the bottom up
  document.querySelectorAll(".is-still").forEach(el => {
    gsap.from(el, {
      clipPath: "inset(100% 0% 0% 0%)",
      scrollTrigger: { trigger: el.closest(".about_image-wrapper") || el, start: "top 80%" },
      delay: 0.3,
      duration: 1.5,
      ease: "sine.out"
    });
  });

  // Award badge: turns slowly while on screen
  document.querySelectorAll(".illo_aaward").forEach(el => {
    if (reduceMotion) return;
    whileVisible(el, gsap.to(el, { rotation: 360, duration: 12, repeat: -1, ease: "none", transformOrigin: "50% 50%", paused: true }));
  });

  // Signature: written left to right
  document.querySelectorAll(".illo_signature").forEach(el => {
    gsap.from(el, {
      clipPath: "inset(0% 100% 0% 0%)",
      scrollTrigger: { trigger: el, start: "top 85%" },
      duration: 1.2,
      ease: "sine.out"
    });
  });
});